/**
 * The dispatch timeline, at the foot of the page and collapsed until asked for.
 *
 * It is a supporting view: it replays the chain against whichever past scenario
 * is selected, so an operator can rehearse the dispatch and watch the tier
 * lines fire against the flood's own arrival time. The clock it drives is the
 * page's, so confirming a recipient on the operations panel records the clock
 * this slider is showing.
 */

import { useEffect } from 'react'

import { SEVERITY_COLOUR, TIER_LABEL, clock } from './shared'
import type { DispatchEvent, SettlementRow } from './shared'
import { C, fs } from '../../design/tokens'

interface Props {
  open: boolean
  onToggle: () => void
  events: DispatchEvent[]
  reached: SettlementRow[]
  t: number
  setT: (value: number) => void
  maxT: number
  playing: boolean
  setPlaying: (value: boolean) => void
  populationReached: { t_min: number; cumulative_population: number }[]
}

const W = 1180
const H = 148

export function DispatchTimeline({
  open,
  onToggle,
  events,
  reached,
  t,
  setT,
  maxT,
  playing,
  setPlaying,
  populationReached,
}: Props) {
  useEffect(() => {
    if (!playing) return
    const timer = window.setInterval(() => {
      setT(t >= maxT ? maxT : t + maxT / 240)
      if (t >= maxT) setPlaying(false)
    }, 50)
    return () => window.clearInterval(timer)
  }, [playing, t, maxT, setT, setPlaying])

  const x = (minutes: number) => 40 + (minutes / maxT) * (W - 70)
  const fired = events.filter((e) => e.t_notified_min <= t).length
  const population =
    populationReached.filter((p) => p.t_min <= t).at(-1)?.cumulative_population ?? 0

  return (
    <section className="panel" style={{ padding: 0 }} data-testid="dispatch-timeline">
      <button
        type="button"
        onClick={onToggle}
        className="panel-header flex w-full items-center gap-3"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}
      >
        <span style={{ color: 'var(--ink-text-dim)', fontSize: 'var(--fs-cell)' }}>
          {open ? '▾' : '▸'}
        </span>
        <span className="panel-title">Dispatch timeline · rehearsal</span>
        <span
          className="font-mono"
          style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-faint)' }}
        >
          {events.length} dispatches over {clock(maxT)}
        </span>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-3">
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="ctl"
              onClick={() => {
                if (t >= maxT) setT(0)
                setPlaying(!playing)
              }}
            >
              {playing ? 'Pause' : 'Play'}
            </button>
            <input
              type="range"
              min={0}
              max={maxT}
              step={maxT / 400}
              value={t}
              onChange={(event) => {
                setPlaying(false)
                setT(Number(event.target.value))
              }}
              style={{ width: 320, accentColor: 'var(--ink-accent)' }}
            />
            <span
              className="font-mono tabular-nums"
              style={{ fontSize: 'var(--fs-cell)', color: 'var(--ink-text)' }}
            >
              {clock(t)}
            </span>
            <span className="section-label ml-4">Dispatches fired</span>
            <span
              className="font-mono tabular-nums"
              style={{ fontSize: 'var(--fs-cell)', color: 'var(--ink-accent)' }}
            >
              {fired} / {events.length}
            </span>
            {populationReached.length > 0 && (
              <>
                <span className="section-label ml-4">Population reached</span>
                <span
                  className="font-mono tabular-nums"
                  style={{ fontSize: 'var(--fs-cell)', color: 'var(--ink-accent)' }}
                >
                  {population.toLocaleString('en-IN')}
                </span>
              </>
            )}
          </div>

          <div className="overflow-x-auto">
            <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{ maxWidth: '100%' }}>
              <line x1={x(0)} x2={x(maxT)} y1={116} y2={116} stroke="var(--ink-border)" />
              {[0, 0.25, 0.5, 0.75, 1].map((f) => (
                <g key={f}>
                  <line
                    x1={x(maxT * f)}
                    x2={x(maxT * f)}
                    y1={116}
                    y2={121}
                    stroke="var(--ink-border)"
                  />
                  <text x={x(maxT * f)} y={138} fontSize={fs(12)} fill={C.textFaint} textAnchor="middle">
                    {clock(maxT * f)}
                  </text>
                </g>
              ))}
              {[1, 2, 3].map((tier) => {
                const times = events.filter((e) => e.tier === tier).map((e) => e.t_notified_min)
                if (!times.length) return null
                const first = Math.min(...times)
                const live = first <= t
                return (
                  <g key={tier}>
                    <line
                      x1={x(first)}
                      x2={x(first)}
                      y1={16 + tier * 24}
                      y2={116}
                      stroke={live ? 'var(--ink-accent)' : C.borderStrong}
                      strokeWidth={2}
                    />
                    <text
                      x={x(first) + 6}
                      y={14 + tier * 24}
                      fontSize={fs(12)}
                      fill={live ? C.text : C.textFaint}
                    >
                      {TIER_LABEL[tier]}
                    </text>
                  </g>
                )
              })}
              {reached.map((s) => (
                <g key={s.settlement_id}>
                  <line
                    x1={x(s.arrival_time_min as number)}
                    x2={x(s.arrival_time_min as number)}
                    y1={10}
                    y2={116}
                    stroke={SEVERITY_COLOUR[s.alert.severity ?? 'Minor']}
                    strokeDasharray="4 3"
                  />
                  <text
                    x={x(s.arrival_time_min as number) - 6}
                    y={108}
                    fontSize={fs(12)}
                    fill={SEVERITY_COLOUR[s.alert.severity ?? 'Minor']}
                    textAnchor="end"
                  >
                    flood at {s.name}
                  </text>
                </g>
              ))}
              <line x1={x(t)} x2={x(t)} y1={6} y2={122} stroke={C.text} strokeWidth={1.5} />
            </svg>
          </div>
        </div>
      )}
    </section>
  )
}
